if (!customElements.get("sticky-product-nav")) {
  customElements.define(
    "sticky-product-nav",
    class StickyProductNav extends HTMLElement {
      constructor() {
        super();

        this.handleResize = this.handleResize.bind(this);
        this.triggers = [];
        this.stickyObserver = null;
        this.activeIndex = 0;
        this.trackX = 0;
        this.isDraggingTabs = false;
        this.lockActiveByClick = false;
        this._resizeTimeout = null;
      }

      connectedCallback() {
        this.parentEl = this.closest(".product__gallery-blocks");
        if (!this.parentEl) return;

        this.blocks = [...this.parentEl.children].filter((el) =>
          el.id?.startsWith("ExtendedBlock-")
        );
        if (this.blocks.length === 0) {
          this.remove();
          return;
        }

        this.render();
        this.initPointerDrag();

        this.waitAndInitialize();

        window.addEventListener("resize", this.handleResize);
      }

      disconnectedCallback() {
        window.removeEventListener("resize", this.handleResize);

        if (this.triggers && this.triggers.length) {
          this.triggers.forEach((t) => t.kill());
          this.triggers = [];
        }

        if (this.stickyObserver) {
          this.stickyObserver.disconnect();
          this.stickyObserver = null;
        }
      }

      waitAndInitialize() {
        this.style.opacity = "0";
        this.style.pointerEvents = "none";

        const init = () => {
          let lastScrollY = window.scrollY;
          let stableCount = 0;

          const checkStable = () => {
            const currentScrollY = window.scrollY;

            if (Math.abs(currentScrollY - lastScrollY) < 1) {
              stableCount++;

              if (stableCount >= 2) {
                this.initialize();
                return;
              }
            } else {
              stableCount = 0;
            }

            lastScrollY = currentScrollY;
            setTimeout(checkStable, 100);
          };

          checkStable();
        };

        if (document.readyState === "complete") {
          setTimeout(init, 100);
        } else {
          window.addEventListener("load", () => setTimeout(init, 100), {
            once: true,
          });
        }
      }

      initialize() {
        if (
          typeof gsap === "undefined" ||
          typeof ScrollTrigger === "undefined"
        ) {
          return;
        }

        this.setInitialActiveBlock();

        this.observeActiveBlock();

        this.observeStickyBar();

        this.style.opacity = "";
        this.style.pointerEvents = "";
        this.classList.add("sticky-product-nav-initialized");
      }

      render() {
        this.innerHTML = `
          <div class="sticky-product-nav-viewport">
            <div class="sticky-product-nav-track"></div>
          </div>
        `;

        this.viewport = this.querySelector(".sticky-product-nav-viewport");
        this.track = this.querySelector(".sticky-product-nav-track");
        this.tabs = [];

        this.blocks.forEach((block, index) => {
          const title = block.dataset.tabHeading || `Tab ${index + 1}`;

          const tab = document.createElement("div");
          tab.className = "sticky-product-nav-item";
          tab.textContent = title;

          tab.addEventListener("click", (e) => {
            if (this.isDraggingTabs) return;
            this.lockActiveByClick = true;
            this.setActiveTab(index);
            this.scrollToElement(block);
            this.centerTab(index);
            setTimeout(() => {
              this.lockActiveByClick = false;
            }, 500);
          });

          this.track.appendChild(tab);
          this.tabs.push(tab);
        });
      }

      initPointerDrag() {
        let isDown = false;
        let startX = 0;
        let initialTrackX = 0;

        const viewport = this.viewport;
        const track = this.track;

        const getBounds = () => ({
          min: viewport.offsetWidth - track.scrollWidth,
          max: 0,
        });

        const setX = (x) => {
          const { min, max } = getBounds();
          const clamped = Math.min(max, Math.max(min, x));
          track.style.transform = `translateX(${clamped}px)`;
          this.trackX = clamped;
        };

        this.trackX = 0;
        this.isDraggingTabs = false;

        track.addEventListener("pointerdown", (e) => {
          isDown = true;
          this.isDraggingTabs = false;

          startX = e.clientX;
          initialTrackX = this.trackX;
        });

        track.addEventListener("pointermove", (e) => {
          if (!isDown) return;

          const delta = e.clientX - startX;

          if (Math.abs(delta) > 4) {
            this.isDraggingTabs = true;
          }

          setX(initialTrackX + delta);
        });

        const endDrag = (e) => {
          if (!isDown) return;
          isDown = false;

          setTimeout(() => {
            this.isDraggingTabs = false;
          }, 50);
        };

        track.addEventListener("pointerup", endDrag);
        track.addEventListener("pointercancel", endDrag);
        track.addEventListener("pointerleave", endDrag);
      }

      centerTab(index) {
        const tab = this.tabs[index];
        if (!tab || !this.viewport || !this.track) return;

        const viewportRect = this.viewport.getBoundingClientRect();
        const tabRect = tab.getBoundingClientRect();

        const delta =
          viewportRect.width / 2 -
          (tabRect.left - viewportRect.left) -
          tabRect.width / 2;

        const finalX = this.trackX + delta;

        const min = this.viewport.offsetWidth - this.track.scrollWidth;
        const max = 0;
        const targetX = Math.min(max, Math.max(min, finalX));

        this.track.style.transition = "transform 0.35s ease";
        this.track.style.transform = `translateX(${targetX}px)`;
        this.trackX = targetX;

        setTimeout(() => {
          this.track.style.transition = "";
        }, 350);
      }

      setInitialActiveBlock() {
        const barRect = this.getBoundingClientRect();
        const barHeight = barRect?.height || 0;
        const activationY = window.innerHeight - 24 - barHeight / 2;

        let foundIndex = 0;

        for (let i = 0; i < this.blocks.length; i++) {
          const block = this.blocks[i];
          const rect = block.getBoundingClientRect();
          const blockTop = rect.top;
          const blockBottom = rect.bottom;

          if (blockTop <= activationY && blockBottom >= activationY) {
            foundIndex = i;
            break;
          }

          if (blockTop > activationY && i > 0) {
            foundIndex = i - 1;
            break;
          }
        }

        this.setActiveTab(foundIndex);
        this.centerTab(foundIndex);
      }

      observeActiveBlock() {
        if (
          typeof gsap === "undefined" ||
          typeof ScrollTrigger === "undefined"
        ) {
          return;
        }

        gsap.registerPlugin(ScrollTrigger);

        if (this.triggers && this.triggers.length) {
          this.triggers.forEach((t) => t.kill());
          this.triggers = [];
        }

        const barRect = this.getBoundingClientRect();
        const barHeight = barRect?.height || 0;
        const activationY = window.innerHeight - 24 - barHeight / 2;
        if (!Number.isFinite(activationY)) return;

        this.triggers = [];

        this.blocks.forEach((block, index) => {
          const trigger = ScrollTrigger.create({
            trigger: block,
            start: () => `top ${activationY}px`,
            end: () => `bottom ${activationY}px`,
            onEnter: () => this.activateFromScroll(index),
            onEnterBack: () => this.activateFromScroll(index),
          });

          this.triggers.push(trigger);
        });
      }

      activateFromScroll(index) {
        if (this.lockActiveByClick) return;
        this.setActiveTab(index);
        this.centerTab(index);
      }

      setActiveTab(index) {
        this.activeIndex = index;
        this.tabs.forEach((t) => t.classList.remove("active"));
        if (this.tabs[index]) {
          this.tabs[index].classList.add("active");
        }
      }

      handleResize() {
        clearTimeout(this._resizeTimeout);
        this._resizeTimeout = setTimeout(() => {
          const prevActive = this.activeIndex;

          this.observeStickyBar();
          this.observeActiveBlock();

          this.setActiveTab(prevActive);
          this.centerTab(prevActive);
        }, 200);
      }

      scrollToElement(target) {
        if (!target) return;

        const headerHeight =
          parseFloat(
            getComputedStyle(document.documentElement).getPropertyValue(
              "--header-height"
            )
          ) || 0;

        const offsetTop = headerHeight + 24;
        const offsetBottom = 24;

        const rect = target.getBoundingClientRect();
        const desiredTop = window.scrollY + rect.top - offsetTop;

        const parentRect = this.parentEl.getBoundingClientRect();
        const parentBottom = window.scrollY + parentRect.bottom;

        const maxScroll = parentBottom - (window.innerHeight - offsetBottom);
        const finalScroll = Math.min(desiredTop, maxScroll);

        window.scrollTo({
          top: finalScroll,
          behavior: "smooth",
        });
      }

      observeStickyBar() {
        if (this.stickyObserver) {
          this.stickyObserver.disconnect();
          this.stickyObserver = null;
        }

        const stickyAddToCartBar = document.querySelector(
          ".product-sticky-add-bar:not(.product-sticky-add-bar--hidden-on-mobile)"
        );
        if (!stickyAddToCartBar) return;

        this.stickyObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (window.innerWidth < 990) {
                if (entry.isIntersecting) {
                  const height = this.offsetHeight || 0;
                  stickyAddToCartBar.style.bottom = `${12 + height + 12}px`;
                } else {
                  stickyAddToCartBar.removeAttribute("style");
                }
              } else {
                stickyAddToCartBar.removeAttribute("style");
              }
            });
          },
          {
            threshold: 0.01,
          }
        );

        this.stickyObserver.observe(this);
      }
    }
  );
}
